import { Web3Provider } from '@ethersproject/providers';
import { formatUnits } from '@ethersproject/units';
import { Info } from '@phosphor-icons/react';
import { useEffect, useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { Constants, Currency, Network, StringUtils } from '@manifoldxyz/js-ts-utils';
import { getSupportedRelayNetworks } from '@/common/networkUtils';
import { GetQuoteParametersWithoutChainId, RequiredFund } from '@/common/types';
import { useERC20Metadata } from '@/hooks/useERC20Metadata';
import useMainStore from '@/store/mainStore';
import RelayNetworkDropdown from './RelayNetworkDropdown';

interface PurchaseRequiredFundsRelayNetworkDropdownProps {
  missingFunds: RequiredFund;
  onNetworkSwitch?: (networkId: number) => void;
  onError?: (message: string) => void;
}

/**
 * PurchaseRequiredFundsRelayNetworkDropdown
 * 1. Prompt user to switch to the required network.
 * 2. Estimate total costs (ERC20 + gas + mintFee).
 * 3. Fetch and display user balances.
 * 4. Calculate and display missing amounts.
 * 5. Show options to load (bridge/swap/purchase) if needed.
 * 6. On success, update relay config and move to the next step.
 */
export default function PurchaseRequiredFundsRelayNetworkDropdown({
  missingFunds,
  onNetworkSwitch,
  onError,
}: PurchaseRequiredFundsRelayNetworkDropdownProps) {
  const connectedNetwork = useMainStore((state) => state.connectedNetwork);
  const { erc20Metadata } = useERC20Metadata({
    networkId: missingFunds.networkId,
    address: missingFunds.address,
  });

  const [expanded, setExpanded] = useState(false);

  const [formattedMissingFunds, setFormattedMissingFunds] = useState<string>();

  useEffect(() => {
    if (!formattedMissingFunds && connectedNetwork && missingFunds) {
      formatMissingFunds(missingFunds).then((amount) => {
        setFormattedMissingFunds(amount);
      });
    }
  }, [connectedNetwork, missingFunds]);

  const parameters: GetQuoteParametersWithoutChainId | undefined = useMemo(() => {
    if (!connectedNetwork) return;
    return {
      toChainId: missingFunds.networkId,
      currency: '0x0000000000000000000000000000000000000000',
      toCurrency: missingFunds.address,
      tradeType: 'EXACT_OUTPUT' as const,
      amount: missingFunds.amount.toString(),
    };
  }, [connectedNetwork, missingFunds]);

  const supportedNetworks = getSupportedRelayNetworks(Network.isTestnet(missingFunds.networkId));

  return !connectedNetwork || !parameters ? (
    <Skeleton height={68} />
  ) : (
    <div className="m-claim--flex m-claim--flex-col m-claim--gap-2">
      {formattedMissingFunds && (
        <div className="m-claim--flex m-claim--items-center m-claim--gap-2">
          <Info
            size={16}
            weight="bold"
            className="m-claim--text-info"
          />
          <p className="m-claim--text-sm m-claim--text-secondary">
            You need <span className="m-claim--font-bold">{formattedMissingFunds}</span> on{' '}
            <span className="m-claim--font-bold">
              {StringUtils.capitalizeFirstLetter(
                Network.getDisplayNameForNetworkId(missingFunds.networkId),
              )}
            </span>{' '}
            to mint
          </p>
        </div>
      )}
      <RelayNetworkDropdown
        availableNetworks={supportedNetworks}
        selectedNetworkId={connectedNetwork}
        parameters={parameters}
        onSelect={onNetworkSwitch}
        expanded={expanded}
        setExpanded={setExpanded}
        columnHeader1={erc20Metadata ? `Buy ${erc20Metadata.symbol} with` : undefined}
        columnHeader2="Balance"
        onError={(error) => {
          onError?.(error);
        }}
      />
    </div>
  );
}

async function formatMissingFunds(missingFund: RequiredFund) {
  if (missingFund.address === Constants.NULL_ADDRESS) {
    return Currency.commifiedRoundedNumberString(
      formatUnits(missingFund.amount, missingFund.decimals),
      4,
    );
  }
  const provider = window.ManifoldEthereumProvider.provider();
  try {
    const erc20Metadata = await Currency.getERC20Metadata(
      missingFund.networkId,
      missingFund.address,
      provider as Web3Provider,
    );
    return `${Currency.commifiedRoundedNumberString(
      formatUnits(missingFund.amount, erc20Metadata.decimals),
      4,
    )} ${erc20Metadata.symbol}`;
  } catch (e) {
    console.warn('Error getting required funds message', e);
  }
}
